import _startCase from 'lodash/startCase';
import _sortBy from 'lodash/sortBy';

import { getSortableStatementPeriodLabel, sortByEarliestDate } from '..';

/**
 * Takes an array of {@link QrrStatementHistoryEntityData} objects and maps it onto three different
 * data structures:
 * - `chartData`, an array of {@link QRRChartData} objects for rendering a QuarterlyRunRate chart
 * - `seriesConfigs`, an object whose keys are entity ids and values are {@link QRRSeriesConfig}
 *   objects. This is used to set up the LineSeries for each entity in the QuarterlyRunRate chart
 *   initialization logic
 * - `tableData`, an array of {@link QRRTableData} objects. This is used to render a table below
 *   the QuarterlyRunRate chart which illustrates how the data is summed to calculate the overall
 *   quarterly run rate for billing purposes
 *
 * These three data structures are wrapped in an object as the return value.
 *
 * @remarks
 * amCharts needs a flat data structure to render a chart; it accepts only a simple key for each
 * value it looks up on each element of the data array. To work within this constraint, we have to
 * map each QrrStatementHistoryEntityData into a flat object. Each of these objects is added to
 * the `result.chartData` array. Since we don't know the keys that we will need to reference until
 * we are mapping over the data, we also have to create a secondary object called `dataKeys` so that
 * we can programmatically tell amCharts to add a lineSeries for each entity. This is contained
 * within a wrapper of type {@link QRRSeriesConfig} which also includes the `entityName` which is
 * used to configure the legend. See test cases in: {@link ./tests/mapQRRHistoryToChartData.test.ts}
 *
 * @param data - array of {@link QrrStatementHistoryEntityData} objects
 * @returns an object containing `chartData`, `seriesConfigs` and `tableData`
 */
export const mapQRRHistoryToChartDetails = (
    data: QrrStatementHistoryEntityData[],
): QRRHistoryChartDetails => {
    const historyDataByEntityId: Record<string, InsightStatementHistoryData[]> =
        {};
    const entitiesById: Record<string, TrqkEntity> = {};

    data.forEach((entityData: QrrStatementHistoryEntityData): void => {
        const entityId = entityData.entity?.id ?? null;
        const currentData = entityData?.data ?? [];

        if (entityId) {
            if (historyDataByEntityId[entityId] === undefined) {
                historyDataByEntityId[entityId] = currentData;
            } else {
                historyDataByEntityId[entityId] =
                    historyDataByEntityId[entityId].concat(currentData);
            }

            if (entitiesById[entityId] === undefined && entityData.entity) {
                entitiesById[entityId] = entityData.entity;
            }
        }
    });

    const chartPeriodDataMap: Record<string, QRRChartPeriodData> = {};

    const tableData: QRRTableData[] = [];

    for (const [entityId, entityHistoryData] of Object.entries(
        historyDataByEntityId,
    )) {
        const entity = entitiesById[entityId] ?? null;

        if (entity !== null) {
            const historyData = entityHistoryData.slice();

            /* eslint-disable no-loop-func */
            historyData.sort(
                (
                    a: InsightStatementHistoryData,
                    b: InsightStatementHistoryData,
                ): number =>
                    sortByEarliestDate(
                        a?.period?.end_date ?? null,
                        b?.period?.end_date ?? null,
                    ),
            );

            let totalEarnings = 0;
            let mostRecentEarnings = 0;
            let mostRecentPeriod: ProStatementPeriod | null = null;

            const entityName = entity?.name ?? null;

            const latestEndDate =
                historyData[historyData.length - 1].period.end_date;

            if (
                entityName !== null &&
                entityId !== null &&
                latestEndDate !== null
            ) {
                const startCasedEntityName = _startCase(entityName);

                historyData.forEach(
                    (currentHistoryData: InsightStatementHistoryData): void => {
                        const { earnings, period, plays } = currentHistoryData;
                        const { end_date } = period;

                        totalEarnings += earnings ?? 0;

                        if (end_date) {
                            if (chartPeriodDataMap[end_date] === undefined) {
                                chartPeriodDataMap[end_date] = {
                                    values: [],
                                    period: period ?? null,
                                };
                            }

                            const isLastStatement = end_date === latestEndDate;

                            if (isLastStatement) {
                                mostRecentEarnings += earnings ?? 0;
                                mostRecentPeriod = period;
                            }

                            const existingIndex = chartPeriodDataMap[
                                end_date
                            ].values.findIndex(
                                (chartDataValue: QRRChartDataValue): boolean =>
                                    chartDataValue.entityId === entityId,
                            );

                            if (existingIndex < 0) {
                                chartPeriodDataMap[end_date].values.push({
                                    entityId,
                                    entityName: startCasedEntityName,
                                    earnings: earnings ?? 0,
                                    hideSquareBullet: !isLastStatement,
                                    hideCircleBullet: isLastStatement,
                                    plays: plays ?? 0,
                                });
                            } else {
                                chartPeriodDataMap[end_date].values[
                                    existingIndex
                                ].earnings += earnings ?? 0;

                                chartPeriodDataMap[end_date].values[
                                    existingIndex
                                ].plays += plays ?? 0;
                            }
                        }
                    },
                );
                /* eslint-enable no-loop-func */

                if (mostRecentPeriod !== null) {
                    tableData.push({
                        entityId,
                        entityName: startCasedEntityName,
                        totalEarnings,
                        mostRecentEarnings,
                        mostRecentPeriod,
                    });
                }
            }
        }
    }

    const sortedTableData = _sortBy(tableData, 'entityName');
    const seriesConfigs: Record<string, QRRSeriesConfig> = {};
    const chartData = [];

    for (const [endDate, chartPeriodData] of Object.entries(
        chartPeriodDataMap,
    )) {
        const chartDataItem: QRRChartData = {
            date: new Date(endDate),
            displayDate: getSortableStatementPeriodLabel(
                chartPeriodData.period,
                endDate,
            ),
        };

        /* eslint-disable no-loop-func */
        chartPeriodData.values.forEach(
            ({
                entityId,
                entityName,
                earnings,
                hideSquareBullet,
                hideCircleBullet,
                plays,
            }: QRRChartDataValue): void => {
                const keys = {
                    entityId: `${entityId}.entityId`,
                    entityName: `${entityId}.entityName`,
                    earnings: `${entityId}.earnings`,
                    hideSquareBullet: `${entityId}.hideSquareBullet`,
                    hideCircleBullet: `${entityId}.hideCircleBullet`,
                    plays: `${entityId}.plays`,
                };

                seriesConfigs[entityId] = {
                    entityName,
                    dataKeys: {
                        entityId: keys.entityId,
                        entityName: keys.entityName,
                        earnings: keys.earnings,
                        hideSquareBullet: keys.hideSquareBullet,
                        hideCircleBullet: keys.hideCircleBullet,
                        plays: keys.plays,
                    },
                };

                chartDataItem[keys.entityId] = entityId;
                chartDataItem[keys.entityName] = entityName;
                chartDataItem[keys.earnings] = earnings;
                chartDataItem[keys.hideSquareBullet] = hideSquareBullet;
                chartDataItem[keys.hideCircleBullet] = hideCircleBullet;
                chartDataItem[keys.plays] = plays;
            },
        );
        /* eslint-enable no-loop-func */

        chartData.push(chartDataItem);
    }

    chartData.sort((a: QRRChartData, b: QRRChartData): number => {
        if (a.date > b.date) {
            return 1;
        }

        if (a.date < b.date) {
            return -1;
        }

        return 0;
    });

    return {
        chartData,
        tableData: sortedTableData,
        seriesConfigs,
    };
};
